import React from 'react';
import Styled from './styled';

import {
    CheckboxIcon,
    RadioIcon,
} from '../Icon';

export type ButtonProps = {
    /** The label text of the button. */
    label: string;
    /** The value of the button. */
    value: string;
    /** Optional click handler for the button, defaults to passing button value */
    onClick?: (value: string) => void;
    /** Size of the button, defaults to 'normal'. */
    size?: 'small' | 'normal' | 'large';
    /** Variant of the button, defaults to 'button'. */
    variant?: 'button' | 'checkbox' | 'radio';
    /** Whether the button is disabled. */
    disabled?: boolean;
    /** Whether the button is selected. */
    selected?: boolean;
    /** Optional, stretch select options (buttons) to occupy full width (defaults to false)*/
    stretch?: boolean;

};

/**
 * Button component that displays a clickable button with a label and various states.
 * 
 * @param props - The properties to configure the Button component.
 * @returns The rendered Button component.
 */
export const Button: React.FC<ButtonProps> = ({
    label,
    onClick = () => { },
    size = 'normal',
    variant = 'button',
    disabled = false,
    selected = false,
    stretch = false,
    value,
}: ButtonProps): JSX.Element => {
    const handleClick = () => {
        onClick(value);
    };

    return (
        <Styled.Button
            onClick={handleClick}
            size={size}
            selected={selected}
            disabled={disabled}
            value={value}
            aria-label={label}
            tabIndex={0}
            role='button'
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault(); // Prevent default scrolling for Space key
                    handleClick();
                }
            }}
            stretch={stretch}
            variant={variant}
        >
            {variant === 'checkbox' &&
                <Styled.Checkbox selected={selected} disabled={disabled}>
                    {selected && <CheckboxIcon />}
                </Styled.Checkbox>
            }
            {variant === 'radio' &&
                <Styled.Radio selected={selected} disabled={disabled}>
                    {selected && <RadioIcon />}
                </Styled.Radio>
            }
            {label}
        </Styled.Button>
    );
};
